import React from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

function ColumnsTable(props) {
  const { columnsData, tableData, tableName, footerData } = props;
  const [sorting, setSorting] = React.useState([]);
  let defaultData = tableData;

  const rightAlignedColumns = new Set(["debit", "credit", "balance", "kwanza", "incomeEuro", "expenseEuro", "incomeKwanza", "expenseKwanza"]);

  const middleAlignedColumns = new Set(["dia", "seqNumber", "registrationDateString", "incomeDia", "incomeNDoc", "expenseDia", "expenseNDoc"]);
  const boldCenterColumns = ["description", "incomeDescription", "expenseDescription"]; 
  const boldCenterText = ["saldo inicial", "transportar","a transportar"]; 

  const columnWidths = {
    dia: "50px",
    incomeDia: "50px",
    incomeNDoc: "70px",
    expenseDia: "50px",
    expenseNDoc: "70px",
    seqNumber: "60px",
    beneficiaryName: "200px",
    description: "350px",
    debit: "100px",
    credit: "100px",
    kwanza: "120px",
    balance: "120px",
    registrationDateString: "100px"
  };

  // Define columns
  const columns = columnsData.map((col) =>
    columnHelper.accessor(col.accessor, {
      id: col.accessor,
      header: () => (
        <p className="text-sm font-bold text-black dark:text-white">
          {col.Header}
        </p>
      ),
      footer: (info) => {
        const dynamicFooter = footerData && footerData[col.accessor];
        return (
          <p className="text-sm font-bold text-black">
            {dynamicFooter || col.Footer}
          </p>
        );
      },
      cell: (info) => {
        const cellValue = info.getValue()?.toString(); // Get the value of the cell
        const isBoldCenter =
          boldCenterColumns.includes(col.accessor) &&
          boldCenterText.some((text) => new RegExp(text, "i").test(cellValue)); // Check for bold condition
  
        return (
          <p
            className={`text-[12px] ${isBoldCenter ? "font-bold text-center" : "font-normal"} text-black-700 dark:text-white`}
          >
            {col.Header === "Editar" ? "Edit" : info.getValue()}
          </p>
        );
      },
      size: columnWidths[col.accessor] || "auto", 
    })
  );
  


  const [data, setData] = React.useState(() => [...defaultData]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <div className="overflow-x-scroll">
      <table className="w-full">
        {/* Table Header */}
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  onClick={header.column.getToggleSortingHandler()}
                  className="cursor-pointer border border-black bg-gray-100 py-1 pr-4 text-center " style={{ border: '0.5px solid black', width: header.column.columnDef.size }}
                >
                  <div className="items-center justify-between text-xs text-black">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: "",
                      desc: "",
                    }[header.column.getIsSorted()] ?? null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {/* Table Body */}

        <tbody>
          {table
            .getRowModel()
            .rows.slice(0, 50)
            .map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const cellValue = cell.getValue()?.toString().toLowerCase(); // Get cell value in lowercase for comparison
                  const isBoldCenter =
                    boldCenterColumns.includes(cell.column.id) &&
                    boldCenterText.some((text) => cellValue?.includes(text));

                  return (
                    <td
                      key={cell.id}
                      className={`border min-w-[50px] border-black py-1 pr-3 ${isBoldCenter
                          ? "font-bold text-center"
                          : rightAlignedColumns.has(cell.column.id)
                            ? "text-right"
                            : middleAlignedColumns.has(cell.column.id)
                              ? "text-center"
                              : "text-left"
                        }`}
                      style={{ border: "0.5px solid black" }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
        </tbody>


        {/* Table Footer */}
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((footer) => (
                <td
                  key={footer.id}
                  colSpan={footer.colSpan}
                  className={`border min-w-[50px] border-black py-1 pr-3 ${rightAlignedColumns.has(footer.column.id) ? "text-right" : ""
                    }`} style={{ border: '0.5px solid black' }}
                >
                  {flexRender(
                    footer.column.columnDef.footer,
                    footer.getContext()
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
    </div>
  );
}

export default ColumnsTable;
const columnHelper = createColumnHelper();
